import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import GenericFormDrawer from '../../../components/GenericFormDrawer';
import Tabs from '../../../components/ui/Tabs';
import Button from '../../../components/ui/Button';
import { thresholdConditionText } from '../../../components/performance/goalTypes';
import { Badge, Sub } from '../../performance-operations/styles';
import { usePanel } from '../../performance-operations/config';
import {
  completionText, dayOf, fetchGoalDetail, fetchGoalHistory, formatNumber,
} from '../config';

// E04 hedef detayı → güncelleme geçmişi (analiz: "Bant → hedef listesi → hedef detay → güncelleme geçmişi"; "Revizyon,
// gerekçe ve onay akışıyla; eski değer korunur"). Kabuk GenericFormDrawer (salt-okunur; birincil düğme "Kapat" —
// InsightDetailDrawer deseni). Kilometre taşında adımlar (kabul eden/tarih/kanıt), eşikte koşul sürümleri, OKR'de KR'ler,
// tüm tiplerde revizyon geçmişi (eski → yeni) ve güncelleme kayıtları.
const Meta = styled.div`
  display: grid;
  grid-template-columns: 170px minmax(0, 1fr);
  gap: 6px 12px;
  margin-bottom: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Label = styled.div`
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
`;

const SectionTitle = styled.h4`
  margin: 18px 0 8px;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.colors.surface.border};
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Item = styled.div`
  padding: 8px 12px;
  margin-bottom: 6px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bgColor};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const HISTORY_PAGE = 20;

function History({ filters, goalId }) {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const panel = usePanel(() => fetchGoalHistory(filters, goalId, { pageNumber: page, pageSize: HISTORY_PAGE }), [goalId, page], !!goalId);
  useEffect(() => { setItems([]); setPage(1); }, [goalId]);
  useEffect(() => {
    if (panel.data) setItems((prev) => (page === 1 ? panel.data.rows : [...prev, ...panel.data.rows]));
  }, [panel.data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (panel.error) {
    return (
      <Item data-state="error">
        {t('performance.goalAnalytics.panel.error')}
        {' '}
        <Button variant="ghost" onClick={panel.retry}>{t('performance.goalAnalytics.panel.retry')}</Button>
      </Item>
    );
  }
  if (!panel.loading && items.length === 0) return <Sub>{t('performance.goalAnalytics.detail.noHistory')}</Sub>;
  const total = panel.data?.total ?? items.length;
  return (
    <>
      {items.map((h) => (
        <Item key={h.id} data-history={h.id}>
          <div>
            <strong>{t(`goalCheckIns.valueKind.${h.valueKind}`, { defaultValue: h.valueKind })}</strong>
            {`: ${h.oldValue != null ? formatNumber(h.oldValue, i18n.language) : '-'} → ${formatNumber(h.newValue, i18n.language)}`}
          </div>
          <Sub>{`${dayOf(h.createDate)} · ${h.createdByName || '-'}`}</Sub>
          {h.note ? <Sub>{h.note}</Sub> : null}
        </Item>
      ))}
      {items.length < total ? (
        <Button variant="outline" onClick={() => setPage((p) => p + 1)} loading={panel.loading} data-action="history-more">
          {t('performance.goalAnalytics.detail.loadMore')}
        </Button>
      ) : null}
    </>
  );
}

function Details({ d }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const g = d.goal;
  const row = (label, value) => (
    <>
      <Label>{label}</Label>
      <div>{value ?? '-'}</div>
    </>
  );
  return (
    <>
      <Meta>
        {row(t('performance.goalAnalytics.col.employee'), g.employeeName)}
        {row(t('performance.goalAnalytics.detail.type'), t(`performance.goal.goalType.${g.goalType}`, { defaultValue: g.goalType }))}
        {row(t('performance.goalAnalytics.detail.status'), t(`performance.goal.status.${g.status}`, { defaultValue: g.status }))}
        {g.goalType === 'kpi' && row(t('performance.goalAnalytics.col.direction'), g.direction ? t(`performance.goal.kpiDirection.${g.direction}`) : '-')}
        {g.goalType === 'kpi' && row(t('performance.goalAnalytics.col.baseline'), g.baselineValue != null ? formatNumber(g.baselineValue, lang) : '-')}
        {g.goalType === 'kpi' && row(t('performance.goalAnalytics.col.target'), g.targetValue != null ? formatNumber(g.targetValue, lang) : '-')}
        {g.goalType === 'kpi' && row(t('performance.goalAnalytics.col.current'), g.currentValue != null ? formatNumber(g.currentValue, lang) : '-')}
        {g.goalType === 'threshold' && row(t('performance.goal.threshold.condition'),
          thresholdConditionText(t, { kpiIndicatorName: '', kpiUnit: g.unit, thresholdOperator: g.thresholdOperator, thresholdValue: g.targetValue }))}
        {g.goalType === 'threshold' && row(t('performance.goal.threshold.actual'), g.thresholdOperator === 'binary'
          ? (g.thresholdMet == null ? '-' : t(g.thresholdMet ? 'performance.goal.threshold.met' : 'performance.goal.threshold.notMet'))
          : (g.currentValue != null ? formatNumber(g.currentValue, lang) : '-'))}
        {row(t('performance.goalAnalytics.col.completion'), completionText(g, lang) || t('performance.goalAnalytics.kpi.noData'))}
        {g.plannedPct != null && row(t('performance.goalAnalytics.series.plan'), `${formatNumber(g.plannedPct, lang)}%`)}
        {row(t('performance.goalAnalytics.col.weight'), g.weightPct != null ? `${formatNumber(g.weightPct, lang)}%` : '-')}
        {row(t('performance.goalAnalytics.col.due'), (
          <>
            {dayOf(g.dueAt)}
            {g.overdue ? <Badge $tone="danger">{t('performance.goalAnalytics.overdue')}</Badge> : null}
          </>
        ))}
        {row(t('performance.goalAnalytics.col.evidence'), (
          <>
            {dayOf(g.lastUpdateAt)}
            {' '}
            <Badge $tone={g.evidenceFresh ? 'success' : 'danger'}>
              {g.evidenceFresh ? t('performance.goalAnalytics.evidence.fresh') : t('performance.goalAnalytics.evidence.staleDays', { days: g.daysSinceUpdate })}
            </Badge>
          </>
        ))}
        {row(t('performance.goalAnalytics.detail.alignedOrgGoal'), g.alignedOrgGoalTitle || '-')}
        {row(t('performance.goalAnalytics.detail.parentGoal'), d.parentGoalTitle || '-')}
        {row(t('performance.goalAnalytics.detail.childGoals'), d.childGoalCount)}
        {row(t('performance.goalAnalytics.detail.linked'), g.linked ? t('performance.goalAnalytics.filter.linked') : t('performance.goalAnalytics.filter.unlinked'))}
        {row(t('performance.goalAnalytics.col.risk'), (g.riskReasons || []).length === 0 ? '-' : (g.riskReasons || []).map((r) => t(`performance.goalAnalytics.risk.${r}`)).join(', '))}
      </Meta>
      {d.description ? <Sub>{d.description}</Sub> : null}

      {(d.steps || []).length > 0 && (
        <>
          <SectionTitle>{t('performance.goal.milestone.steps')}</SectionTitle>
          {d.steps.map((s) => (
            <Item key={s.id} data-step={s.id}>
              <div>
                <strong>{s.title}</strong>
                {` · ${formatNumber(s.weightPct, lang)}% · `}
                {t(`performance.goal.milestone.status.${s.status}`, { defaultValue: s.status })}
              </div>
              {s.dueDate ? <Sub>{`${t('performance.goal.milestone.stepDueDate')}: ${dayOf(s.dueDate)}`}</Sub> : null}
              {s.evidenceNote ? <Sub>{`${t('performance.goal.milestone.evidence')}: ${s.evidenceNote}`}</Sub> : null}
              {s.evidenceUrl ? <Sub>{s.evidenceUrl}</Sub> : null}
              {s.decidedByName ? <Sub>{t('performance.goal.milestone.acceptedBy', { name: s.decidedByName, date: dayOf(s.decidedAt) })}</Sub> : null}
              {s.decisionNote ? <Sub>{`${t('performance.goal.milestone.decisionNote')}: ${s.decisionNote}`}</Sub> : null}
            </Item>
          ))}
        </>
      )}

      {(d.conditionVersions || []).length > 0 && (
        <>
          <SectionTitle>{t('performance.goalAnalytics.detail.conditionVersions')}</SectionTitle>
          {d.conditionVersions.map((v) => (
            <Item key={v.version} data-version={v.version}>
              <div>
                <strong>{t('performance.goal.threshold.versionInfo', { version: v.version })}</strong>
                {` · ${thresholdConditionText(t, { kpiIndicatorName: v.indicatorName, kpiUnit: v.unit, thresholdOperator: v.operator, thresholdValue: v.thresholdValue })}`}
              </div>
              <Sub>{`${dayOf(v.createDate)} · ${t(`performance.goalAnalytics.detail.conditionSource.${v.source}`, { defaultValue: v.source })}`}</Sub>
            </Item>
          ))}
        </>
      )}

      {(d.keyResults || []).length > 0 && (
        <>
          <SectionTitle>{t('performance.goal.keyResults')}</SectionTitle>
          {d.keyResults.map((k) => (
            <Item key={k.id}>
              <div><strong>{k.title}</strong>{` · ${formatNumber(k.progressPct, lang)}%`}</div>
              {k.managerScore != null ? <Sub>{`${t('performance.goal.managerScore')}: ${k.managerScore}`}</Sub> : null}
            </Item>
          ))}
        </>
      )}

      <SectionTitle>{t('performance.goalAnalytics.detail.revisions')}</SectionTitle>
      {(d.revisions || []).length === 0 ? <Sub>{t('performance.goalAnalytics.detail.noRevisions')}</Sub> : d.revisions.map((r) => (
        <Item key={r.id} data-revision={r.id}>
          <div>
            <strong>{t(`performance.teamGoals.revisionStatus.${r.status}`, { defaultValue: r.status })}</strong>
            {` · ${dayOf(r.createDate)}`}
          </div>
          {Object.entries(r.changes || {}).map(([field, change]) => (
            <Sub key={field}>{`${field}: ${change.old ?? '-'} → ${change.new ?? '-'}`}</Sub>
          ))}
        </Item>
      ))}
    </>
  );
}

export default function GoalDetailDrawer({
  filters, goalId, open, onClose,
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState('detail');
  useEffect(() => { setTab('detail'); }, [goalId]);
  const panel = usePanel(() => fetchGoalDetail(filters, goalId), [JSON.stringify(filters), goalId], open && !!goalId);
  const d = panel.data;

  return (
    <GenericFormDrawer
      open={open}
      onClose={onClose}
      title={d?.goal?.title || t('performance.goalAnalytics.detail.title')}
      schema={[]}
      submitLabel={t('common.close', { defaultValue: 'Kapat' })}
      onSave={async () => true}
    >
      <Tabs
        tabs={[
          { key: 'detail', label: t('performance.goalAnalytics.detail.tabDetail') },
          { key: 'history', label: t('performance.goalAnalytics.detail.tabHistory') },
        ]}
        activeTab={tab}
        onChange={setTab}
      />
      {panel.loading ? <Sub data-state="loading">{t('common.loading')}</Sub> : null}
      {panel.error ? (
        <Item data-state="error">
          {t('performance.goalAnalytics.panel.error')}
          {' '}
          <Button variant="ghost" onClick={panel.retry}>{t('performance.goalAnalytics.panel.retry')}</Button>
        </Item>
      ) : null}
      {d && tab === 'detail' ? <Details d={d} /> : null}
      {goalId && tab === 'history' ? <History filters={filters} goalId={goalId} /> : null}
    </GenericFormDrawer>
  );
}
